import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MessageService, ToastMessageOptions } from "@openng/optimus-ui/api";
import { catchError, Observable, of, OperatorFunction, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ApiErrorHandlerService {
  private readonly message = inject(MessageService);
  private readonly translate = inject(TranslateService);

  public handle<T>(entity?: string): OperatorFunction<T, T>;
  public handle<T, F>(entity: string | undefined, fallback: F): OperatorFunction<T, T | F>;
  public handle<T, F>(entity?: string, fallback?: F): OperatorFunction<T, T | F> {
    return catchError((error: unknown) => {
      const { code, message } = this.getErrorDetails(error);
      this.message.add(this.buildMessage(code, entity));
      if (fallback !== undefined) {
        return of(fallback) as Observable<F>;
      } else {
        return throwError(() => message);
      }
    });
  }

  private getErrorDetails(error: unknown): { code: string; message: string } {
    if (error && typeof error === "object") {
      const errorObject = error as { error?: unknown; message?: unknown; status?: unknown };
      const isHttpError = error instanceof HttpErrorResponse || typeof errorObject.status === "number";

      if (isHttpError) {
        if (errorObject.error && typeof errorObject.error === "object") {
          const backendError = errorObject.error as { code?: unknown; message?: unknown };
          return {
            code: typeof backendError.code === "string" ? backendError.code : "UNKNOWN_ERROR",
            message: typeof backendError.message === "string" ? backendError.message : "Unknown error",
          };
        }

        return {
          code: "UNKNOWN_ERROR",
          message: typeof errorObject.error === "string" ? errorObject.error : "Unknown error",
        };
      }

      if (typeof errorObject.message === "string") {
        return { code: "UNKNOWN_ERROR", message: errorObject.message };
      }
    }

    return {
      code: "UNKNOWN_ERROR",
      message: typeof error === "string" ? error : "Unknown error",
    };
  }

  private buildMessage(code: string, entity?: string): ToastMessageOptions {
    const message: ToastMessageOptions = {
      key: "global",
      severity: "error",
      summary: this.translate.instant("common.server_error"),
      detail: this.translate.instant(`error_messages.${code}`, {
        object: entity ? this.translate.instant(`common.entities.${entity}`) : undefined,
      }),
      life: 4500,
    };

    return message;
  }
}
